import { EMPTY_ARRAY } from '@unifyapps/carbon/consts/empty';
import { ActionType } from '@unifyapps/defs/types/action';
import type { Event } from '@unifyapps/defs/types/event';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { useMemo } from 'react';
import { useGlobalStateStore } from '../../stores/GlobalStateStore';
import { getOriginalBlockId } from '../../utils/runTimeBlocks';

const EMPTY_EVENTS = EMPTY_ARRAY as Event[];

function useDerivedEvents({
  blockStateId = '',
  events = EMPTY_EVENTS,
}: {
  blockStateId?: string;
  events?: Event[];
}) {
  const contextualDialogId = useGlobalStateStore().hooks.block.useContextualDialog(
    getOriginalBlockId(blockStateId),
  );

  const contextualDialogEvents: Event[] = useMemo(() => {
    if (!contextualDialogId) return EMPTY_EVENTS;

    // contextualDialogId will only be present for contextual dialogs with version < 1
    // Version >= 1 contextual dialogs are filtered out when creating the contextualDialogForBlocks map
    return [
      {
        id: 'contextual-dialog-toggle',
        eventType: EventTypeEnum.OnClick,
        action: {
          actionType: ActionType.ControlBlockMethod,
          payload: {
            blockId: contextualDialogId,
            methodName: 'toggleContextualDialog',
            blockStateId,
          },
        },
      },
    ];
  }, [blockStateId, contextualDialogId]);

  // The platform automatically configures derived block events for a block.
  // Place derived events first to ensure they are fired first,
  // as events before them might take too much time and prevent them from firing.
  const allEvents = useMemo(() => {
    if (contextualDialogEvents.length === 0) {
      return events;
    }
    return [...contextualDialogEvents, ...events];
  }, [contextualDialogEvents, events]);

  return { derivedEvents: allEvents };
}

export default useDerivedEvents;
